import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Menu, X, ChevronDown, ChevronUp, Mail, Phone, 
  Shield, TrendingUp, Search, Users, Globe, Award,
  CheckCircle, ArrowRight, Lock
} from 'lucide-react';
import { toast } from 'sonner';

const ADMIN_PASSWORD = 'Admin123';

const LandingPage = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [openFaq, setOpenFaq] = useState(null);

  const handleAdminLogin = () => {
    if (adminPassword === ADMIN_PASSWORD) {
      localStorage.setItem('jta_admin_authenticated', 'true');
      toast.success('Access granted!');
      navigate('/dashboard');
    } else {
      toast.error('Incorrect password');
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const faqs = [
    {
      question: 'WHICH MARKETS DO YOU CURRENTLY SERVE?',
      answer: 'We have head offices in North America, Asia Pacific and Europe, which gives us a broad reach. We are currently well-positioned to serve clients in both emerging and developed markets.'
    },
    {
      question: 'HOW CAN I SET UP A CONSULTATION?',
      answer: 'We offer a free initial consultation to new clients, to better understand their needs and explain our services. Simply email us to book your session.'
    },
    {
      question: 'WHAT SERVICES DO YOU OFFER?',
      answer: 'We specialize in AI tools, software development, cloud computing, IT infrastructure, security solutions, and comprehensive tech consulting tailored to your business needs.'
    },
    {
      question: 'HAVE ADDITIONAL QUESTIONS?',
      answer: 'Contact us directly via email at info@jthomassite.com or call us at 800.318.5922. We\'re here to help!'
    }
  ];

  const values = [
    { title: 'Talent across several geos', icon: Globe },
    { title: 'Most talents hold advanced degrees in tech-related disciplines', icon: Award },
    { title: 'We have provided contractors for large and medium-sized companies', icon: Users },
    { title: 'Customer satisfaction evidenced through contract renewals', icon: CheckCircle }
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-gray-900/95 backdrop-blur-md border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <div className="text-2xl sm:text-3xl font-bold">
                <span className="text-amber-500">J.Thomas</span>
                <span className="text-white">&</span>
              </div>
              <span className="text-xs text-gray-400 hidden sm:block">Associates, Inc.</span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('home')} className="text-gray-300 hover:text-amber-500 transition-colors">Home</button>
              <button onClick={() => scrollToSection('about')} className="text-gray-300 hover:text-amber-500 transition-colors">About</button>
              <button onClick={() => scrollToSection('services')} className="text-gray-300 hover:text-amber-500 transition-colors">Services</button>
              <button onClick={() => scrollToSection('faq')} className="text-gray-300 hover:text-amber-500 transition-colors">FAQ</button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-300 hover:text-amber-500 transition-colors">Contact</button>
              <Button 
                onClick={() => setShowAdminModal(true)}
                className="bg-amber-500 hover:bg-amber-600 text-gray-900 font-semibold rounded-full px-6"
              >
                <Lock className="w-4 h-4 mr-2" />
                Admin
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 text-gray-300"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-gray-800 border-t border-gray-700">
            <div className="px-4 py-4 space-y-3">
              <button onClick={() => scrollToSection('home')} className="block w-full text-left text-gray-300 hover:text-amber-500 py-2">Home</button>
              <button onClick={() => scrollToSection('about')} className="block w-full text-left text-gray-300 hover:text-amber-500 py-2">About</button>
              <button onClick={() => scrollToSection('services')} className="block w-full text-left text-gray-300 hover:text-amber-500 py-2">Services</button>
              <button onClick={() => scrollToSection('faq')} className="block w-full text-left text-gray-300 hover:text-amber-500 py-2">FAQ</button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left text-gray-300 hover:text-amber-500 py-2">Contact</button>
              <Button 
                onClick={() => { setShowAdminModal(true); setMobileMenuOpen(false); }}
                className="w-full bg-amber-500 hover:bg-amber-600 text-gray-900 font-semibold rounded-full"
              >
                <Lock className="w-4 h-4 mr-2" />
                Admin Access
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center pt-20">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1521737711867-e3b97375f902?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/80 to-gray-900/60"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <div className="mb-6">
              <span className="text-amber-500 text-lg font-semibold tracking-wider">JTA</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Your Go-To <span className="text-amber-500">AI & Tech</span> Outsourcing and Consulting Firm
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 mb-8 leading-relaxed">
              Maximizing your technology investments with comprehensive solutions tailored to your specific needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => scrollToSection('contact')}
                className="bg-amber-500 hover:bg-amber-600 text-gray-900 font-semibold text-lg px-8 py-6 rounded-full"
              >
                Get Started
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                variant="outline"
                onClick={() => scrollToSection('about')}
                className="border-2 border-gray-400 text-white hover:bg-white/10 font-semibold text-lg px-8 py-6 rounded-full"
              >
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* JTA Advantage Section */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                alt="Business Meeting"
                className="rounded-2xl shadow-2xl"
              />
            </div>
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Experience the <span className="text-amber-500">JTA Advantage</span>
              </h2>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                Are you looking for a reliable and experienced team to help you maximize your technology investments? Look no further than JTA – the leading provider of comprehensive tech solutions.
              </p>
              <p className="text-gray-300 text-lg leading-relaxed">
                Our team of experts specializes in customizing solutions tailored to your specific needs. Whether it's AI tools, software development, cloud computing, IT infrastructure, or security, we've got you covered. Trust us to deliver the best results and take your business to new heights of success.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                About <span className="text-amber-500">Us</span>
              </h2>
              <div className="space-y-4 text-gray-300 text-lg">
                <p>
                  Established in <span className="text-amber-500 font-semibold">2004</span> and currently operating in Silicon Valley, our company was previously based in the Metro New York Area.
                </p>
                <p>
                  A <span className="text-amber-500 font-semibold">Minority-Owned Business</span> with emphasis on IT Consulting.
                </p>
                <p>
                  Our business is spearheaded by a team of experienced leaders with a cumulative experience of <span className="text-amber-500 font-semibold">25+ years</span>.
                </p>
              </div>
              <div className="mt-8 p-4 bg-gray-800 rounded-xl border border-gray-700">
                <p className="text-sm text-gray-400">
                  Our company is verified by <span className="text-white font-semibold">Dun and Bradstreet</span>, the world's leading provider of business data and insights.
                </p>
              </div>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                alt="Team Collaboration"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Added Value */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Our <span className="text-amber-500">Added Value</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card key={index} className="bg-gray-900 border-gray-700 hover:border-amber-500/50 transition-all">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
                    <value.icon className="w-7 h-7 text-amber-500" />
                  </div>
                  <p className="text-gray-300">{value.title}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services / We Are Section */}
      <section id="services" className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border-amber-500/30 hover:border-amber-500 transition-all">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-amber-500 flex items-center justify-center mx-auto mb-6">
                  <Shield className="w-8 h-8 text-gray-900" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">We Are CREDIBLE</h3>
                <p className="text-gray-300">
                  Our team has consistently met all customer satisfaction requirements, achieving a 100% success rate.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border-amber-500/30 hover:border-amber-500 transition-all">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-amber-500 flex items-center justify-center mx-auto mb-6">
                  <TrendingUp className="w-8 h-8 text-gray-900" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">We Are ROBUST</h3>
                <p className="text-gray-300">
                  Our company is growing and making meaningful progress in the tech industry.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border-amber-500/30 hover:border-amber-500 transition-all">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-amber-500 flex items-center justify-center mx-auto mb-6">
                  <Search className="w-8 h-8 text-gray-900" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">We Are METICULOUS</h3>
                <p className="text-gray-300">
                  We thoroughly examine your company's requirements and rigorously screen all talent.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-gray-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Frequently Asked <span className="text-amber-500">Questions</span>
            </h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-gray-900 rounded-xl border border-gray-700 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-800/50 transition-colors"
                >
                  <span className="font-semibold text-white pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-amber-500 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-amber-500 flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-300">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Revolutionizing the <span className="text-amber-500">Businesses</span> of Tomorrow
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                alt="Contact Us"
                className="rounded-2xl shadow-2xl"
              />
            </div>
            <div className="bg-gray-800 rounded-2xl p-8 border border-gray-700">
              <h3 className="text-2xl font-bold mb-6">
                <span className="text-amber-500">J. Thomas &</span> Associates, Inc
              </h3>
              <div className="space-y-4">
                <a href="mailto:info@jthomassite.com" className="flex items-center gap-3 text-gray-300 hover:text-amber-500 transition-colors">
                  <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-amber-500" />
                  </div>
                  <span>info@jthomassite.com</span>
                </a>
                <a href="tel:8003185922" className="flex items-center gap-3 text-gray-300 hover:text-amber-500 transition-colors">
                  <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-amber-500" />
                  </div>
                  <span>800.318.5922</span>
                </a>
                <p className="text-gray-400 text-sm mt-6">
                  Fax: 831.625.1699
                </p>
              </div>
              <div className="mt-8 pt-6 border-t border-gray-700">
                <p className="text-gray-400 text-sm">
                  Our company is verified by <span className="text-white">Dun and Bradstreet</span>, the world's leading provider of business data and insights.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-gray-950 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-gray-400 text-sm">
              © {new Date().getFullYear()} J. Thomas & Associates, Inc. All rights reserved.
            </div>
            <div className="flex items-center gap-4">
              <span className="text-gray-500 text-sm">Minority-Owned Business</span>
            </div>
          </div>
        </div>
      </footer>

      {/* Admin Login Modal */}
      {showAdminModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-gray-800 rounded-2xl p-8 max-w-md w-full border border-gray-700 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-amber-500" />
              </div>
              <h3 className="text-2xl font-bold text-white">Admin Access</h3>
              <p className="text-gray-400 mt-2">Enter password to access Data Labeling Tool</p>
            </div>
            <div className="space-y-4">
              <Input
                type="password"
                placeholder="Enter password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAdminLogin()}
                className="bg-gray-900 border-gray-600 text-white placeholder:text-gray-500 h-12"
              />
              <Button 
                onClick={handleAdminLogin}
                className="w-full bg-amber-500 hover:bg-amber-600 text-gray-900 font-semibold h-12 rounded-full"
              >
                Access Data Labeling
              </Button>
              <Button 
                variant="ghost"
                onClick={() => { setShowAdminModal(false); setAdminPassword(''); }}
                className="w-full text-gray-400 hover:text-white"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
